/*
 * Decision-tree and answer processing.
 */

const getDefaultState = () => {
  return {
    navigation_history: {},
    is_final_page: false,
    SELECTED_DECISION_TREE: {},
    batch_id: null,
    rules: null,
  }
}

export const state =
  getDefaultState()

const containsKey = (
  object,
  key
) => {
  try {
    return Object.prototype.hasOwnProperty.call(
      object,
      key
    )
  } catch (_) {
    return false
  }
}

const compareEqual = (
  value,
  comparison
) =>
  Number(value) ===
  Number(comparison)

const compareGreater = (
  value,
  comparison
) =>
  Number(value) >=
  Number(comparison)

const compareLess = (
  value,
  comparison
) =>
  Number(value) <
  Number(comparison)

export const mutations = {
  RESET_STATE(state) {
    Object.assign(
      state,
      getDefaultState()
    )
  },

  SET_DECISION_TREE(
    state,
    tree
  ) {
    try {
      state.SELECTED_DECISION_TREE =
        typeof tree === 'string'
          ? JSON.parse(tree)
          : tree || {}
    } catch (_) {
      state.SELECTED_DECISION_TREE =
        {}
    }
  },

  setDistributionType(
    state,
    batchId
  ) {
    state.batch_id =
      batchId
  },

  setPageRules(
    state,
    rules
  ) {
    state.rules =
      rules || null
  },

  setIsFinalPage(
    state,
    isFinal
  ) {
    state.is_final_page =
      Boolean(isFinal)
  },
}

export const actions = {
  setDistributionSettings(
    { commit },
    mode
  ) {
    commit(
      'SET_DECISION_TREE',
      mode && mode.tree
        ? mode.tree
        : {}
    )

    commit(
      'setDistributionType',
      mode
        ? mode.batch
        : null
    )
  },

  async processPageRules(
    {
      state,
      commit,
      dispatch,
      rootGetters,
    },
    currentPage
  ) {
    let rules = null

    try {
      rules =
        state.SELECTED_DECISION_TREE &&
        state.SELECTED_DECISION_TREE[
          currentPage
        ]
          ? state.SELECTED_DECISION_TREE[
              currentPage
            ]
          : null
    } catch (_) {
      rules = null
    }

    commit(
      'setPageRules',
      rules
    )

    if (
      !rules ||
      typeof rules !== 'object'
    ) {
      commit(
        'setIsFinalPage',
        false
      )

      return
    }

    commit(
      'setIsFinalPage',
      containsKey(
        rules,
        'final'
      ) &&
        rules.final === true
    )

    if (
      containsKey(
        rules,
        'feedback'
      )
    ) {
      commit(
        'feedback/setFeedbackRules',
        rules.feedback,
        {
          root: true,
        }
      )
    }

    const inReview =
      rootGetters[
        'navigation/isReviewMode'
      ] === true

    /*
     * Review must never rerun route-changing or unlock logic.
     */
    if (inReview) {
      try {
        await dispatch(
          'feedback/rehydrateForReview',
          null,
          {
            root: true,
          }
        )
      } catch (_) {}

      return
    }

    if (
      !containsKey(
        rules,
        'load'
      ) ||
      !rules.load
    ) {
      return
    }

    const loadRules =
      rules.load

    if (
      containsKey(
        loadRules,
        'score'
      )
    ) {
      const scoreAction =
        String(
          loadRules.score || ''
        )

      if (scoreAction) {
        await dispatch(
          scoreAction.includes('/')
            ? scoreAction
            : scoreAction,
          {},
          {
            root:
              scoreAction.includes(
                '/'
              ),
          }
        )
      }

      return
    }

    if (
      containsKey(
        loadRules,
        'actions'
      ) &&
      Array.isArray(
        loadRules.actions
      )
    ) {
      for (
        const action of
          loadRules.actions
      ) {
        if (
          action &&
          action.key
        ) {
          await dispatch(
            `feedback/${action.key}`,
            {},
            {
              root: true,
            }
          )
        }
      }

      return
    }

    if (
      containsKey(
        loadRules,
        'next'
      ) &&
      loadRules.next
    ) {
      await dispatch(
        'navigation/setNextPage',
        loadRules.next,
        {
          root: true,
        }
      )
    }
  },

  /*
   * Process a submitted page answer.
   *
   * Every nested action is awaited so the next page cannot mount
   * before profile values and routing state are ready.
   */
  async processAnswers(
    {
      state,
      dispatch,
      rootGetters,
    },
    payload
  ) {
    const rules =
      state.rules || {}

    const inReview =
      rootGetters[
        'navigation/isReviewMode'
      ] === true

    if (inReview) {
      return
    }

    if (
      containsKey(
        rules,
        'completed'
      ) &&
      rules.completed
    ) {
      const completed =
        rules.completed

      if (
        containsKey(
          completed,
          'actions'
        )
      ) {
        const configuredActions =
          completed.actions

        let actions = null

        if (
          Array.isArray(
            configuredActions
          )
        ) {
          actions =
            configuredActions[
              payload.question_no || 0
            ]
        } else {
          actions =
            configuredActions
        }

        if (actions) {
          await dispatch(
            'processActions',
            {
              answers:
                payload.answers,

              actions,
            }
          )
        }
      } else if (
        containsKey(
          completed,
          'process'
        ) &&
        completed.process
      ) {
        await dispatch(
          'dispatchRequest',
          {
            key:
              `feedback/${completed.process}`,

            value: 0,
          }
        )
      }

      if (
        containsKey(
          completed,
          'score'
        ) &&
        completed.score
      ) {
        await dispatch(
          'processScores',
          completed.score
        )
      } else if (
        completed.next
      ) {
        await dispatch(
          'navigation/setNextPage',
          completed.next,
          {
            root: true,
          }
        )
      }

      return
    }

    if (
      !containsKey(
        rules,
        'conditions'
      ) ||
      !rules.conditions ||
      !containsKey(
        rules.conditions,
        payload.data_id
      )
    ) {
      return
    }

    const conditions =
      rules.conditions[
        payload.data_id
      ]

    const answerGroups =
      Array.isArray(
        payload.answers
      )
        ? payload.answers
        : [payload.answers]

    for (
      const condition of
        conditions || []
    ) {
      for (
        const currentAnswer of
          answerGroups
      ) {
        const answerValue =
          Array.isArray(
            currentAnswer
          )
            ? currentAnswer[
                condition.index
              ]
            : currentAnswer

        if (
          condition.answer !==
          answerValue
        ) {
          continue
        }

        if (condition.next) {
          await dispatch(
            'navigation/setNextPage',
            condition.next,
            {
              root: true,
            }
          )
        }

        if (
          condition.actions
        ) {
          const action =
            Array.isArray(
              condition.actions
            )
              ? condition.actions[
                  payload.question_no ||
                    0
                ]
              : condition.actions

          if (action) {
            await dispatch(
              'processActions',
              {
                answers:
                  condition.answer,

                actions:
                  action,
              }
            )
          }
        }

        return
      }
    }
  },

  async processActions(
    { dispatch },
    payload
  ) {
    const actions =
      payload.actions

    if (
      !actions ||
      !actions.key
    ) {
      return
    }

    const value =
      containsKey(
        actions,
        'value'
      )
        ? actions.value
        : payload.answers

    await dispatch(
      'dispatchRequest',
      {
        key:
          `profile/${actions.key}`,

        value,
      }
    )
  },

  async processScores(
    {
      dispatch,
      rootGetters,
    },
    scores
  ) {
    const inReview =
      rootGetters[
        'navigation/isReviewMode'
      ] === true

    if (
      inReview ||
      !Array.isArray(scores)
    ) {
      return
    }

    for (
      const scoreRule of scores
    ) {
      const conditions =
        scoreRule.conditions || []

      const value =
        rootGetters[
          `profile/${scoreRule.key}`
        ]

      for (
        const condition of
          conditions
      ) {
        let confirmed = false

        if (
          condition.operator ===
          'greater'
        ) {
          confirmed =
            compareGreater(
              value,
              condition.answer
            )
        } else if (
          condition.operator ===
          'less'
        ) {
          confirmed =
            compareLess(
              value,
              condition.answer
            )
        } else {
          confirmed =
            compareEqual(
              value,
              condition.answer
            )
        }

        if (
          confirmed &&
          condition.next
        ) {
          await dispatch(
            'navigation/setNextPage',
            condition.next,
            {
              root: true,
            }
          )

          return
        }
      }
    }
  },

  async dispatchRequest(
    {
      commit,
      dispatch,
    },
    payload
  ) {
    if (
      !payload ||
      !payload.key
    ) {
      return
    }

    const actionExists =
      this._actions &&
      this._actions[
        payload.key
      ]

    if (actionExists) {
      await dispatch(
        payload.key,
        payload.value,
        {
          root: true,
        }
      )

      return
    }

    commit(
      payload.key,
      payload.value,
      {
        root: true,
      }
    )
  },

  async startActivity(
    {
      rootGetters,
    },
    payload
  ) {
    const user =
      rootGetters[
        'auth/getUser'
      ]

    if (
      !user ||
      !this.$ga ||
      typeof this.$ga.event !==
        'function'
    ) {
      return
    }

    try {
      await this.$ga.event(
        `Started ${payload.eventCategory}`,
        payload.eventCategory,
        user.user_uuid,
        payload.eventCategory
      )
    } catch (_) {}
  },

  async endActivity(
    {
      rootGetters,
    },
    payload
  ) {
    const user =
      rootGetters[
        'auth/getUser'
      ]

    if (
      !user ||
      !this.$ga ||
      typeof this.$ga.event !==
        'function'
    ) {
      return
    }

    try {
      await this.$ga.event(
        `Ended ${payload.eventCategory}`,
        payload.eventCategory,
        user.user_uuid,
        payload.eventCategory
      )
    } catch (_) {}
  },
}

export const getters = {
  getIsFinalPage:
    (state) =>
      state.is_final_page,
}